import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import { backendDomain } from "../utils";
import universe from "../images/project-giphy.gif";
import Chip from "@mui/material/Chip";

const MainContainer = styled.div`
  height: 100%;
  min-height: 100vh;
`;

const ContentContainer = styled.div`
  margin-top: 83px;
  margin-left: 60px;
  margin-right: 60px;
`;

const PageTitle = styled.div`
  font-size: 2rem;
  font-weight: bold;
  padding-bottom: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PageSubTitle = styled.div`
  font-size: 1rem;
  padding-bottom: 5rem;
`;

// Custom styled CardContent with black background and white font color
const CustomCardContent = styled(CardContent)`
  background-color: black;
  color: white;

  transition: background-color 0.3s ease;
  &:hover {
    cursor: pointer;
    background-color: grey;
  }
`;

const CustomCardActions = styled(CardActions)`
  background-color: black;
  color: white;
`;

// Add frosted glass card and grid styles
const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2.5rem;
  margin: 3rem 0 4rem 0;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FrostedCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 340px;
  border-radius: 1.5rem;
  overflow: hidden;
  background: linear-gradient(
    135deg,
    rgba(30, 30, 40, 0.85),
    rgba(20, 20, 30, 0.85)
  );
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  border: 1.5px solid rgba(144, 202, 249, 0.18);
  backdrop-filter: blur(18px) saturate(180%);
  -webkit-backdrop-filter: blur(18px) saturate(180%);
  transition: box-shadow 0.1s cubic-bezier(0.4, 0.2, 0.2, 1),
    border 0.1s cubic-bezier(0.4, 0.2, 0.2, 1);
  cursor: pointer;
  &:hover {
    /* No transform or scale */
    border: 1.5px solid #90caf9;
    box-shadow: 0 0 0 4px rgba(144, 202, 249, 0.25),
      0 8px 32px rgba(0, 0, 0, 0.18);
  }
`;

const CardImage = styled.div`
  position: relative;
  width: 100%;
  height: 180px;
  background: ${({ src }) =>
    src && !src.endsWith(".gif")
      ? `url(${src}) center/cover no-repeat`
      : `linear-gradient(120deg, #444 40%, #888 100%)`};
  border-bottom: 1.5px solid rgba(255, 255, 255, 0.13);
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.08) 60%,
      rgba(30, 30, 40, 0.38) 100%
    );
    pointer-events: none;
  }
  overflow: hidden;
`;

const CardContentSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 1.5rem 1.3rem 1.2rem 1.3rem;
  color: #fff;
`;

const ChipsRow = styled.div`
  margin-bottom: 0.7rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const CardTitle = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.4rem;
  letter-spacing: -0.01em;
  line-height: 1.2;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
`;

const CardSubtitle = styled.div`
  font-size: 1.02rem;
  color: #e3f2fd;
  margin-bottom: 0.7rem;
  font-weight: 400;
  line-height: 1.4;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
`;

const CardMeta = styled.div`
  font-size: 0.92rem;
  color: #b3b3b3;
  margin-top: auto;
  font-weight: 400;
  letter-spacing: 0.01em;
`;

const CardCredit = styled.div`
  position: absolute;
  bottom: 0.5rem;
  right: 0.7rem;
  color: #fff;
  background: rgba(0, 0, 0, 0.45);
  padding: 0.18rem 0.6rem;
  border-radius: 0.3rem;
  font-size: 0.78rem;
  z-index: 2;
  text-align: right;
`;

// Utility function to check if a URL is a valid image
const isValidImageUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  // Basic check for image extensions
  return /^(https?:\/\/).+\.(jpg|jpeg|png|gif|webp|svg)$/i.test(url);
};

const SectionHeader = styled.div`
  font-size: 2.2rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  margin: 3.5rem 0 2.2rem 0;
  padding-left: 0.2em;
  color: #fff;
  background: linear-gradient(
    90deg,
    rgba(144, 202, 249, 0.18) 0%,
    rgba(30, 30, 40, 0.08) 100%
  );
  border-radius: 1.2rem;
  box-shadow: 0 2px 12px rgba(144, 202, 249, 0.08);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: inline-block;
`;

const ProjectLandingPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(`${backendDomain()}/project/all`);
        const data = await response.json();

        // Transform the data into an array
        const postsArray = Object.entries(data).map(([slug, post]) => ({
          slug,
          ...post,
        }));
        setPosts(postsArray);
      } catch (error) {
        console.error("Error fetching posts:", error);
      }
    };

    fetchPosts();
  }, []);

  const readMoreHandler = (blog) => {
    navigate(`/project/${blog.slug}`);
  };

  const Paragraph = styled.div`
    margin-bottom: 1rem;

    margin-left: auto;
    margin-right: auto;
    max-width: 50rem;
    font-size: 1.125rem;
    line-height: 150%;
    font-weight: 400;
    font-family: sans-serif;
  `;

  return (
    <MainContainer>
      <ContentContainer>
        <PageTitle>
          {/* <img src={universe} alt="reading" width="100" height="100" /> */}
          <Paragraph>
            "Funktionslust" is this awesome German word that means finding joy
            in doing what you're best at. For me, building projects is all about
            curiosity, tinkering, and not being afraid to fail along the way. My
            approach boils down to four things: keeping it fun and exciting with
            new ideas, diving into tough challenges that push boundaries, making
            a positive impact by building fair, bias-free tools, and creating
            something useful for the community that others can enjoy and build
            on.
          </Paragraph>
        </PageTitle>
        {posts.length > 0 ? (
          <>
            {/* Split posts into featured and others */}
            {(() => {
              const featuredProjects = posts
                .filter((post) => post.data && post.data.featured === true)
                .sort((a, b) => {
                  return new Date(b.data.date) - new Date(a.data.date);
                });

              const otherProjects = posts
                .filter((post) => !post.data?.featured)
                .sort((a, b) => {
                  return new Date(b.data.date) - new Date(a.data.date);
                });
              return (
                <>
                  {featuredProjects.length > 0 && (
                    <>
                      <SectionHeader>Featured Projects</SectionHeader>
                      <ProjectsGrid
                        style={{ marginTop: "1.5rem", marginBottom: "3.5rem" }}
                      >
                        {featuredProjects.map((post) => {
                          const imgSrc = post.data.thumbnail || universe;
                          const isGif = imgSrc.endsWith(".gif");
                          const showImage = isValidImageUrl(
                            post.data.thumbnail
                          );
                          return (
                            <FrostedCard
                              key={post.slug}
                              onClick={() => readMoreHandler(post)}
                            >
                              <CardImage src={imgSrc}>
                                {showImage ? (
                                  isGif ? (
                                    <img
                                      src={imgSrc}
                                      alt="thumbnail"
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        zIndex: 0,
                                      }}
                                    />
                                  ) : null
                                ) : (
                                  <div
                                    style={{
                                      background: "black",
                                      width: "100%",
                                      height: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      position: "absolute",
                                      left: 0,
                                      top: 0,
                                      zIndex: 0,
                                      padding: "0 1.2rem",
                                    }}
                                  >
                                    <span
                                      style={{
                                        color: "#fff",
                                        fontWeight: 700,
                                        fontSize: "1.25rem",
                                        textAlign: "center",
                                        textShadow:
                                          "0 2px 8px rgba(0,0,0,0.18)",
                                        display: "-webkit-box",
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        whiteSpace: "normal",
                                        maxWidth: "95%",
                                        lineHeight: 1.25,
                                      }}
                                    >
                                      {post.data.title}
                                    </span>
                                  </div>
                                )}
                                {post.data.thumbnail_credit && showImage && (
                                  <CardCredit>
                                    Img Credit:
                                    <br />
                                    {post.data.thumbnail_credit}
                                  </CardCredit>
                                )}
                              </CardImage>
                              <CardContentSection>
                                {post?.data?.chips &&
                                  post?.data?.chips?.length > 0 && (
                                    <ChipsRow>
                                      {post.data.chips.map((chip, index) => (
                                        <Chip
                                          key={index}
                                          label={chip}
                                          sx={{
                                            border: "1px solid #fff",
                                            color: "#fff",
                                            fontSize: "0.85rem",
                                            height: "24px",
                                            background:
                                              "rgba(255,255,255,0.08)",
                                            fontWeight: 500,
                                          }}
                                          variant="outlined"
                                        />
                                      ))}
                                    </ChipsRow>
                                  )}
                                <CardTitle>{post.data.title}</CardTitle>
                                <CardSubtitle>
                                  {post.data.subtitle}
                                </CardSubtitle>
                                <CardMeta>
                                  {(() => {
                                    const dateObj = new Date(post.data.date);
                                    return !isNaN(dateObj) ? (
                                      <>
                                        Date:{" "}
                                        {dateObj.toLocaleDateString(undefined, {
                                          year: "numeric",
                                          month: "long",
                                          day: "numeric",
                                        })}
                                      </>
                                    ) : null;
                                  })()}
                                </CardMeta>
                              </CardContentSection>
                            </FrostedCard>
                          );
                        })}
                      </ProjectsGrid>
                    </>
                  )}
                  {otherProjects.length > 0 && (
                    <>
                      <SectionHeader
                        style={{
                          marginTop:
                            featuredProjects.length > 0 ? "0" : "3.5rem",
                        }}
                      >
                        Other Projects
                      </SectionHeader>
                      <ProjectsGrid>
                        {otherProjects.map((post) => {
                          const imgSrc = post.data.thumbnail || universe;
                          const isGif = imgSrc.endsWith(".gif");
                          const showImage = isValidImageUrl(
                            post.data.thumbnail
                          );
                          return (
                            <FrostedCard
                              key={post.slug}
                              // FIXME: temporarily disable the click behavior till I add the projects bakc
                              // onClick={() => readMoreHandler(post)}
                            >
                              <CardImage src={imgSrc}>
                                {showImage ? (
                                  isGif ? (
                                    <img
                                      src={imgSrc}
                                      alt="thumbnail"
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        zIndex: 0,
                                      }}
                                    />
                                  ) : null
                                ) : (
                                  <div
                                    style={{
                                      background: "black",
                                      width: "100%",
                                      height: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      position: "absolute",
                                      left: 0,
                                      top: 0,
                                      zIndex: 0,
                                      padding: "0 1.2rem",
                                    }}
                                  >
                                    <span
                                      style={{
                                        color: "#fff",
                                        fontWeight: 700,
                                        fontSize: "1.25rem",
                                        textAlign: "center",
                                        textShadow:
                                          "0 2px 8px rgba(0,0,0,0.18)",
                                        display: "-webkit-box",
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        whiteSpace: "normal",
                                        maxWidth: "95%",
                                        lineHeight: 1.25,
                                      }}
                                    >
                                      {post.data.title}
                                    </span>
                                  </div>
                                )}
                                {post.data.thumbnail_credit && showImage && (
                                  <CardCredit>
                                    Img Credit:
                                    <br />
                                    {post.data.thumbnail_credit}
                                  </CardCredit>
                                )}
                              </CardImage>
                              <CardContentSection>
                                {post?.data?.chips &&
                                  post?.data?.chips?.length > 0 && (
                                    <ChipsRow>
                                      {post.data.chips.map((chip, index) => (
                                        <Chip
                                          key={index}
                                          label={chip}
                                          sx={{
                                            border: "1px solid #fff",
                                            color: "#fff",
                                            fontSize: "0.85rem",
                                            height: "24px",
                                            background:
                                              "rgba(255,255,255,0.08)",
                                            fontWeight: 500,
                                          }}
                                          variant="outlined"
                                        />
                                      ))}
                                    </ChipsRow>
                                  )}
                                <CardTitle>{post.data.title}</CardTitle>
                                <CardSubtitle>
                                  {post.data.subtitle}
                                </CardSubtitle>
                                <CardMeta>
                                  {(() => {
                                    const dateObj = new Date(post.data.date);
                                    return !isNaN(dateObj) ? (
                                      <>
                                        Date:{" "}
                                        {dateObj.toLocaleDateString(undefined, {
                                          year: "numeric",
                                          month: "long",
                                          day: "numeric",
                                        })}
                                      </>
                                    ) : null;
                                  })()}
                                </CardMeta>
                              </CardContentSection>
                            </FrostedCard>
                          );
                        })}
                      </ProjectsGrid>
                    </>
                  )}
                </>
              );
            })()}
          </>
        ) : (
          <div
            style={{
              textAlign: "center",
              color: "#b3b3b3",
              fontSize: "1.2rem",
              margin: "4rem 0",
            }}
          >
            loading...
          </div>
        )}
      </ContentContainer>
    </MainContainer>
  );
};

export default ProjectLandingPage;
